import { useState } from "react";
import { ArrowUpTrayIcon, DocumentTextIcon } from "@heroicons/react/24/outline";

interface CsvDropzoneProps {
  onFile: (file: File) => void;
  fileName?: string | null;
  disabled?: boolean;
}

/**
 * File picker for the CSV upload step: click, keyboard, or drag and drop.
 *
 * A `<label>` wrapping the input is what makes click and keyboard work — the browser
 * already forwards a label click to its control, and the input stays focusable and
 * announced as a file input. The input is `sr-only` rather than `hidden` on purpose:
 * `display: none` would take it out of the tab order and there would be no keyboard
 * path to the picker at all.
 *
 * Reports only the picked file; every decision about whether the contents are usable
 * belongs to the parse step, so a wrong file can be replaced without leaving here.
 */
export default function CsvDropzone({
  onFile,
  fileName = null,
  disabled = false,
}: CsvDropzoneProps) {
  const [dragging, setDragging] = useState(false);

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragging(false);
    if (disabled) return;
    const file = event.dataTransfer.files?.[0];
    if (file) onFile(file);
  };

  return (
    <label
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      className={`flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-10 text-center transition focus-within:ring-2 focus-within:ring-orange focus-within:ring-offset-2 ${
        disabled
          ? "border-gray-200 bg-gray-50 cursor-not-allowed opacity-60"
          : dragging
            ? "border-orange bg-orange/5 cursor-pointer"
            : "border-gray-300 bg-white hover:border-orange hover:bg-orange/5 cursor-pointer"
      }`}
    >
      <input
        type="file"
        accept=".csv,text/csv"
        className="sr-only"
        disabled={disabled}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onFile(file);
          // Reset so re-picking the same file after a failed parse still fires change.
          event.target.value = "";
        }}
      />

      {fileName ? (
        <>
          <DocumentTextIcon className="h-8 w-8 text-orange" aria-hidden="true" />
          <p className="text-dark-grey font-semibold text-sm sm:text-base break-all">
            {fileName}
          </p>
          <p className="text-xs sm:text-sm text-gray-500">
            Click or drop another file to replace it
          </p>
        </>
      ) : (
        <>
          <ArrowUpTrayIcon className="h-8 w-8 text-gray-400" aria-hidden="true" />
          <p className="text-dark-grey font-semibold text-sm sm:text-base">
            Drop a CSV file here, or click to browse
          </p>
          <p className="text-xs sm:text-sm text-gray-500">
            Nothing is uploaded until you review the rows on the next step
          </p>
        </>
      )}
    </label>
  );
}
